/**
 * 文件上传 API
 * 
 * 支持两种上传方式：
 * 1. 通过后端代理上传（默认）- 后端转发到 R2
 * 2. 直接上传到 R2（需要配置预签名 URL 或公开写入权限）
 */

import { tokenManager } from './auth';
import { appConfig } from '../config';

// 类型定义
export interface UploadResponse {
  code: number;
  msg: string;
  data: string;  // 上传后的文件URL
}

export interface PresignedUrlResponse {
  code: number;
  msg: string;
  data: {
    uploadUrl: string;   // 预签名上传 URL
    fileUrl: string;     // 文件访问 URL
  };
}

/**
 * 生成唯一文件名
 */
const generateFileName = (file: File): string => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 8);
  const ext = file.name.split('.').pop() || 'jpg';
  return `${timestamp}_${random}.${ext}`;
};

/**
 * 方式1: 通过后端代理上传（推荐）
 * 后端接口负责转发到 R2
 */
export const uploadViaBackend = async (file: File): Promise<UploadResponse> => {
  const formData = new FormData();
  formData.append('file', file);

  const headers = tokenManager.getAuthHeader();

  const response = await fetch(`${appConfig.api.baseUrl}/infra/file/upload`, {
    method: 'POST',
    headers: {
      ...headers,
      // 注意：不要设置 Content-Type，让浏览器自动设置 multipart/form-data
    },
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`上传失败: ${response.status}`);
  }

  return response.json();
};

/**
 * 方式2: 获取预签名 URL 后直接上传到 R2
 * 步骤：1. 从后端获取预签名 URL  2. 使用预签名 URL 上传到 R2
 */
export const uploadViaPresignedUrl = async (file: File): Promise<UploadResponse> => {
  const fileName = generateFileName(file);
  
  // 步骤1: 从后端获取预签名 URL
  const headers = tokenManager.getAuthHeader();
  const presignResponse = await fetch(
    `${appConfig.api.baseUrl}/infra/file/presign?fileName=${encodeURIComponent(fileName)}&contentType=${encodeURIComponent(file.type)}`,
    {
      method: 'GET',
      headers,
    }
  );

  if (!presignResponse.ok) {
    throw new Error(`获取上传地址失败: ${presignResponse.status}`);
  }

  const presignData: PresignedUrlResponse = await presignResponse.json();
  
  if (presignData.code !== 0 || !presignData.data) {
    throw new Error(presignData.msg || '获取上传地址失败');
  }

  // 步骤2: 使用预签名 URL 直接上传到 R2
  const uploadResponse = await fetch(presignData.data.uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type,
    },
    body: file,
  });

  if (!uploadResponse.ok) {
    throw new Error(`上传到 R2 失败: ${uploadResponse.status}`);
  }

  return {
    code: 0,
    msg: '',
    data: presignData.data.fileUrl,
  };
};

/**
 * 方式3: 直接上传到 R2（需要配置 R2 端点和公开写入权限）
 * 注意：这种方式不安全，仅用于测试
 */
export const uploadDirectToR2 = async (file: File): Promise<UploadResponse> => {
  const { endpoint, publicDomain, bucketName } = appConfig.r2;
  
  if (!endpoint || !bucketName) {
    throw new Error('R2 配置不完整，请检查 VITE_R2_ENDPOINT 和 VITE_R2_BUCKET_NAME');
  }

  const fileName = generateFileName(file);
  const uploadUrl = `${endpoint}/${bucketName}/${fileName}`;

  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type,
    },
    body: file,
  });

  if (!response.ok) {
    throw new Error(`上传到 R2 失败: ${response.status}`);
  }

  // 返回公共访问 URL
  const fileUrl = publicDomain 
    ? `${publicDomain}/${fileName}`
    : `${endpoint}/${bucketName}/${fileName}`;

  return {
    code: 0,
    msg: '',
    data: fileUrl,
  };
};

/**
 * 统一上传接口
 * 自动选择上传方式：
 * 1. 如果配置了 R2，使用直接上传
 * 2. 否则使用后端代理上传
 */
export const uploadImage = async (file: File): Promise<UploadResponse> => {
  // 优先使用后端代理上传（更安全）
  return uploadViaBackend(file);
  
  // 如果需要使用预签名 URL 方式，可以改为：
  // return uploadViaPresignedUrl(file);
  
  // 如果需要直接上传到 R2（不推荐），可以改为：
  // return uploadDirectToR2(file);
};

/**
 * 批量上传图片
 */
export const uploadImages = async (
  files: File[],
  onProgress?: (completed: number, total: number) => void
): Promise<string[]> => {
  const urls: string[] = [];
  
  for (let i = 0; i < files.length; i++) {
      const response = await uploadImage(files[i]);
    
    if (response.code === 0 && response.data) {
      urls.push(response.data);
      } else {
      throw new Error(response.msg || `上传第 ${i + 1} 张图片失败`);
      }
      
      if (onProgress) {
        onProgress(i + 1, files.length);
    }
  }
  
  return urls;
};
